import JSZip from 'jszip';
import { db, getJoinedOfflineInspections } from '../services/db';

/**
 * Packs all unsynced local inspections and their corresponding binary images
 * into a single ZIP archive, and downloads it.
 *
 * File Structure inside ZIP:
 * - data.json (form metadata list)
 * - areas.json (area lookup data)
 * - zones.json (zone lookup data)
 * - images/ (folder containing image binary files)
 */
export const exportOfflineDataToZip = async (plantName = 'plant') => {
  try {
    const records = await getJoinedOfflineInspections();
    if (records.length === 0) {
      alert("No pending offline inspections to export.");
      return;
    }

    const zip = new JSZip();
    const imagesFolder = zip.folder("images");

    const jsonMetadata = [];

    for (const record of records) {
      let relativeImageName = null;

      if (record.image && record.image.data) {
        // Construct a unique filename for the zip images folder
        const extension = record.image.type.split('/')[1] || 'png';
        relativeImageName = `image_${record.id}.${extension}`;

        // Add binary image file directly to the zip folder
        imagesFolder.file(relativeImageName, record.image.data, { binary: true });
      }

      // Add record to metadata listing (linking the image filename)
      jsonMetadata.push({
        id: record.id,
        trial_id: record.trial_id,
        area_id: record.area_id,
        zone_id: record.zone_id,
        xrfNumber: record.xrfNumber,
        Ni: record.Ni,
        remarksValue: record.remarksValue,
        comments: record.comments,
        date: record.date,
        name: record.name,
        imageName: relativeImageName,
        imageType: record.image ? record.image.type : null
      });
    }

    // Also export area and zone lookup tables so import can reconstruct joins
    const areas = await db.area.toArray();
    const zones = await db.zone.toArray();

    // Add JSON metadata files to the zip
    zip.file("data.json", JSON.stringify(jsonMetadata, null, 2));
    zip.file("areas.json", JSON.stringify(areas, null, 2));
    zip.file("zones.json", JSON.stringify(zones, null, 2));

    // Generate zip blob
    const content = await zip.generateAsync({ type: "blob" });

    // Trigger browser file download
    const cleanPlantName = plantName.toLowerCase().replace(/[^a-z0-9]/g, '_');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `inspections_backup_${cleanPlantName}_${timestamp}.zip`;

    const link = document.createElement('a');
    link.href = URL.createObjectURL(content);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    console.log("ZIP backup generated and downloaded successfully.");
  } catch (error) {
    console.error("Failed to export offline ZIP backup:", error);
    alert("An error occurred while generating the ZIP backup.");
  }
};

/**
 * Reads an uploaded ZIP archive, extracts the metadata, binary images,
 * and area/zone lookup data, then restores them into the Dexie IndexedDB tables.
 */
export const importZipFileToDexie = async (zipFile) => {
  try {
    const zip = await JSZip.loadAsync(zipFile);
    const metadataFile = zip.file("data.json");

    if (!metadataFile) {
      throw new Error("Invalid backup ZIP: 'data.json' metadata is missing.");
    }

    const metadataText = await metadataFile.async("text");
    const records = JSON.parse(metadataText);

    if (!Array.isArray(records)) {
      throw new Error("Invalid backup metadata format.");
    }

    // 1. Read lookup tables from ZIP (non-DB async tasks)
    const areasFile = zip.file("areas.json");
    let areas = [];
    if (areasFile) {
      const areasText = await areasFile.async("text");
      areas = JSON.parse(areasText);
    }

    const zonesFile = zip.file("zones.json");
    let zones = [];
    if (zonesFile) {
      const zonesText = await zonesFile.async("text");
      zones = JSON.parse(zonesText);
    }

    // 2. Pre-extract all image binary files from the ZIP (non-DB async tasks)
    const extractedImages = {};
    for (const record of records) {
      if (record.imageName) {
        const imgZipFile = zip.file(`images/${record.imageName}`);
        if (imgZipFile) {
          const imgBuffer = await imgZipFile.async("arraybuffer");
          extractedImages[record.imageName] = imgBuffer;
        }
      }
    }

    // 3. Perform database operations in a single fast transaction (no non-DB awaits inside!)
    await db.transaction('rw', [db.area, db.zone, db.image, db.trial, db.trialDetails], async () => {
      // Restore area lookup table
      if (areas.length > 0) {
        await db.area.bulkPut(areas);
      }

      // Restore zone lookup table
      if (zones.length > 0) {
        await db.zone.bulkPut(zones);
      }

      // Restore inspection records
      for (const record of records) {
        let localImageId = null;

        // Use pre-extracted image binary data
        if (record.imageName && extractedImages[record.imageName]) {
          const imgBuffer = extractedImages[record.imageName];
          
          // Add image back to Dexie and get key
          localImageId = await db.image.add({
            name: record.imageName,
            type: record.imageType || 'image/png',
            size: imgBuffer.byteLength,
            data: imgBuffer
          });
        }

        // Put trial header
        await db.trial.put({
          id: record.trial_id,
          area_id: record.area_id,
          created_at: new Date(record.date)
        });

        // Put trial details linked to restored image
        await db.trialDetails.put({
          id: record.id,
          trial_id: record.trial_id,
          zone_id: record.zone_id,
          img_id: localImageId,
          xrfNumber: record.xrfNumber,
          Ni: record.Ni,
          remarksValue: record.remarksValue,
          comments: record.comments,
          date: record.date,
          name: record.name
        });
      }
    });

    console.log(`Successfully restored ${records.length} records from ZIP backup.`);
    return records.length;
  } catch (error) {
    console.error("Failed to import offline ZIP backup:", error);
    throw error;
  }
};
